import type { ReactNode } from 'react';
import { AlertCircle, Check, HardDriveDownload, Loader2, WifiOff } from 'lucide-react';

/**
 * Canonical save-status vocabulary shared across the app. Owns the single
 * source of truth for which icon/label/color renders for each status so the
 * various save UIs (autosave toasts, inline form indicators) never drift.
 */
export type SaveStatus =
  | 'idle'
  | 'dirty'
  | 'saving'
  | 'saved'
  | 'error'
  | 'savedLocally';

type SaveStatusMeta = {
  className: string;
  icon: ReactNode;
  text: string;
};

const SAVE_STATUS_META: Record<Exclude<SaveStatus, 'idle'>, SaveStatusMeta> = {
  dirty: {
    className: 'bg-gray-100 text-gray-600 border-gray-200',
    icon: <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />,
    text: 'Unsaved',
  },
  saving: {
    className: 'bg-blue-50 text-blue-600 border-blue-200',
    icon: <Loader2 className="h-3 w-3 animate-spin" />,
    text: 'Saving...',
  },
  saved: {
    className: 'bg-green-50 text-green-600 border-green-200',
    icon: <Check className="h-3 w-3" />,
    text: 'Saved',
  },
  error: {
    className: 'bg-red-50 text-red-600 border-red-200',
    icon: <AlertCircle className="h-3 w-3" />,
    text: 'Error saving',
  },
  savedLocally: {
    className: 'bg-amber-50 text-amber-600 border-amber-200',
    icon: <HardDriveDownload className="h-3 w-3" />,
    text: 'Saved locally',
  },
};

const PILL_BASE =
  'text-xs px-3 py-1.5 rounded-full border shadow-sm flex items-center gap-1.5';

/** Renders the save-status pill for a given status. Returns null when idle. */
export function SaveStatusPill({ status }: { status: SaveStatus }) {
  if (status === 'idle') return null;
  const meta = SAVE_STATUS_META[status];

  return (
    <span className={`${PILL_BASE} ${meta.className}`}>
      {meta.icon}
      {meta.text}
    </span>
  );
}

/** Network-offline pill. Distinct from the `savedLocally` status. */
export function OfflinePill() {
  return (
    <span className={`${PILL_BASE} bg-red-50 text-red-700 border-red-200`}>
      <WifiOff className="h-3 w-3" />
      Offline
    </span>
  );
}

/** Collapse boolean form state into the canonical save status. */
export function toSaveStatus({
  isSaving,
  isDirty,
  hasOfflineEdits,
  hasContent,
}: {
  isSaving: boolean;
  isDirty: boolean;
  hasOfflineEdits: boolean;
  hasContent: boolean;
}): SaveStatus {
  if (isSaving) return 'saving';
  if (hasOfflineEdits) return 'savedLocally';
  if (isDirty) return 'dirty';
  if (hasContent) return 'saved';
  return 'idle';
}
